/**
 * §7.7's cross-document compliance view — "admins see a cross-document
 * compliance view".
 *
 * Reaching it at all is `audit.view`, which §4.4 gives to `admin` alone, and it
 * is one of the few checks in this system with no subject: the view spans every
 * document, so there is no node to resolve against and picking one to stand for
 * the rest would be inventing an ACL. The rows are still joined against
 * `permission_visible_nodes`, so holding the capability is not the same as being
 * shown documents the grants do not reach — this is "everything outstanding that
 * you can reach", never "everything outstanding".
 *
 * **Nothing here gates the page on a role.** A person without `audit.view` gets
 * the API's 403 rendered as itself, carrying the capability and the resolution
 * that produced it, which is a better answer than a screen that quietly does not
 * exist — and it keeps the one copy of the rule in SQL.
 *
 * The default order is the server's and it matters: incomplete cycles first,
 * then by due date. A compliance page whose first screen is finished work
 * answers the question it exists for badly.
 */

import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useAcknowledgmentCompliance } from '../../api/queries.ts';
import { Button } from '../../components/ui/button.tsx';
import { EmptyState, Loading, PageError } from '../../components/ui/feedback.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.tsx';
import { CycleProgressView } from './progress.tsx';
import { AudienceDescription, CycleScopeBadge, DueDate } from './vocabulary.tsx';

const PAGE_SIZE = 50;

export function CompliancePage() {
  const [outstandingOnly, setOutstandingOnly] = useState(false);
  const [includeSuperseded, setIncludeSuperseded] = useState(false);
  const [offset, setOffset] = useState(0);

  const query = useAcknowledgmentCompliance({
    outstandingOnly,
    includeSuperseded,
    limit: PAGE_SIZE,
    offset,
  });

  if (query.isError) {
    return (
      <PageError
        error={query.error}
        title="Acknowledgment compliance"
        back={{ href: '/acknowledgments', label: 'What you owe' }}
      />
    );
  }

  const cycles = query.data?.cycles ?? [];
  const total = query.data?.total ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  /** Any filter change resets to the first page; page four of a new filter reads as empty. */
  const refilter = (change: () => void) => {
    setOffset(0);
    change();
  };

  return (
    <>
      <PageHeader
        title="Acknowledgment compliance"
        description={
          <>
            Every open cycle across every document you can reach, each one scoped to the version it
            was opened against. Audiences are re-resolved on every load, so a person provisioned
            this morning is already in the denominators below.
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-1 rounded-lg border border-primary p-1">
            <Button
              size="sm"
              variant={outstandingOnly ? 'primary' : 'tertiary'}
              onPress={() => refilter(() => setOutstandingOnly((value) => !value))}
            >
              Outstanding only
            </Button>
            <Button
              size="sm"
              variant={includeSuperseded ? 'primary' : 'tertiary'}
              onPress={() => refilter(() => setIncludeSuperseded((value) => !value))}
            >
              Include superseded
            </Button>
          </div>
        }
      />

      <Card padded={false}>
        {query.isPending ? (
          <Loading label="Resolving every audience…" />
        ) : cycles.length === 0 ? (
          <EmptyState
            title="No cycles to show"
            description={
              outstandingOnly
                ? 'Nothing outstanding. Every current cycle you can reach has been acknowledged by everyone its audience resolves to.'
                : 'Nobody has opened an acknowledgment cycle on a document you can reach. They are opened from a published document, by whoever can publish it.'
            }
          />
        ) : (
          <div className={query.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-secondary px-5 py-3">
              <p className="text-sm text-tertiary">
                Showing <span className="font-medium text-secondary">{from}</span>–
                <span className="font-medium text-secondary">{to}</span> of{' '}
                <span className="font-medium text-secondary">{total}</span>
                {includeSuperseded ? ', including cycles their version has outlived' : ''}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  isDisabled={offset === 0}
                  onPress={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  isDisabled={to >= total}
                  onPress={() => setOffset(offset + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>

            <Table>
              <THead>
                <TR>
                  <TH>Document</TH>
                  <TH>Cycle</TH>
                  <TH>Audience</TH>
                  <TH>Due</TH>
                  <TH>Progress</TH>
                  <TH>{''}</TH>
                </TR>
              </THead>
              <TBody>
                {cycles.map((cycle) => (
                  <TR key={cycle.requestId}>
                    <TD>
                      <Link
                        to="/documents/$nodeId"
                        params={{ nodeId: cycle.documentId }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {cycle.title}
                      </Link>
                    </TD>
                    <TD>
                      <CycleScopeBadge cycle={cycle} />
                    </TD>
                    <TD className="text-xs text-tertiary">
                      <AudienceDescription audience={cycle.audience} />
                    </TD>
                    <TD className="text-xs">
                      <DueDate dueDate={cycle.dueDate} />
                    </TD>
                    <TD>
                      <CycleProgressView progress={cycle.progress} compact />
                    </TD>
                    <TD>
                      <Link
                        to="/acknowledgments/$requestId"
                        params={{ requestId: cycle.requestId }}
                        className="text-sm link"
                      >
                        Who owes it
                      </Link>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </Card>
    </>
  );
}
